'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { useContext } from 'react';
import { v4 as uuid } from 'uuid';

import { postPositionApi } from '@flipster/apis/v2/trade';
import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { useVariant } from '@flipster/features/src/shared/ab-test';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { Button } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { refDetailTimes } from '@flipster/socket/r230522/server-time';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import ExternalLinkIcon from '@flipster/ui/assets/svgs/external-link.svg';
import { CurrencyPrecisionMapper, maxDecimalPrecision } from '@flipster/utils/constants/currency';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import { countDecimalPlaces } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { useSymbolMarginType } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';

import { hedgingOrderLeverage, hedgingOrderSide } from '../constants';
import { HedgingFormContext, hedgingFormBunja } from './state';

interface PositionOrderButtonProps {
  onClose: () => void;
}

export const PositionOrderButton = ({ onClose }: PositionOrderButtonProps) => {
  const router = useRouter();
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { symbolInfoAtom, orderPriceAtom, pswapDetailAtom } = useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const orderPrice = useAtomValue(orderPriceAtom);
  const pswapDetail = useAtomValue(pswapDetailAtom);
  const attribution = useAtomValue(orderAttributionAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);

  if (!symbolInfo) return null;

  const { symbol, displayShortName } = symbolInfo;

  const submit = async () => {
    const requestId = uuid();
    const timestamp = dateToEpochString(new Date());
    const refTimestamp = refDetailTimes[symbol];
    if (!pswapDetail?.bidPrice) return;

    try {
      await postPositionApi(symbol, {
        side: hedgingOrderSide,
        requestId,
        timestamp,
        ...refTimestamp,
        qty: orderPrice,
        leverage: hedgingOrderLeverage,
        price: pswapDetail.bidPrice,
        attribution,
        marginType,
      });
      onClose();
      router.push(`/trade/${symbol}`);
      snackbar.success({ message: `${displayShortName} order has executed` });
    } catch (err) {
      snackbar.error({ message: getErrorMessage(err) });
    }
  };

  return (
    <SegmentClick event={StakeEvent.StakeHedgeOrderClick} data={{ currency: stakingCurrency }}>
      <Button
        as='button'
        type='submit'
        onClick={submit}
        hierarchy='primary'
        color='sell'
        size='large'
        label={`Short ${stakingCurrency}`}
      />
    </SegmentClick>
  );
};

export const PositionOrderCard = () => {
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { symbolInfoAtom, orderPriceAtom, marginAtom, pswapDetailAtom, takerFeeRateAtom, liqShortPriceAtom } =
    useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const orderPrice = useAtomValue(orderPriceAtom);
  const margin = useAtomValue(marginAtom);
  const pswapDetail = useAtomValue(pswapDetailAtom);
  const takerFeeRate = useAtomValue(takerFeeRateAtom);
  const liqShortPrice = useAtomValue(liqShortPriceAtom);

  const crossMarginFeatureEnabled = useVariant('tf-cross-margin-and-cross-asset') === 'on';

  if (!symbolInfo) return null;

  const { quoteCurrency, unitOrderQty } = symbolInfo;

  return (
    <div className={css({ mt: 16, p: 12, bgColor: 'background.default.level-0', rounded: 8 })}>
      <div className={flex({ alignItems: 'center', gap: 8 })}>
        <CurrencyDisplay
          currency={stakingCurrency}
          icon={<CurrencyDisplay.Icon size={24} space={8} />}
          mainLabel={
            <CurrencyDisplay.Label
              value='shortName'
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-4' }}
            />
          }
        />
        <OrderSideBadge orderSide={hedgingOrderSide} />
        <LeverageBadge leverage={hedgingOrderLeverage} />
      </div>
      <dl className={css({ mt: 12, textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
        <div className={sectionDescriptionItem}>
          <dt>Quantity</dt>
          <dd className={stack({ alignItems: 'flex-end' })}>
            <Formatted.Number
              value={orderPrice}
              suffix={stakingCurrency}
              precision={countDecimalPlaces(unitOrderQty)}
              toFixed
              css={sectionMainPrice}
            />
            <Formatted.Number
              value={margin}
              suffix={quoteCurrency}
              precision={CurrencyPrecisionMapper[quoteCurrency]}
              prefix='≈ '
              css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-2' }}
            />
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt>Margin</dt>
          <dd>
            <Formatted.Number
              value={margin}
              suffix={quoteCurrency}
              precision={CurrencyPrecisionMapper[quoteCurrency]}
              toFixed
              css={sectionMainPrice}
            />
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt className={hstack({ alignItems: 'center', gap: 4 })}>
            <IsomorphicLink
              href='/support/guide/fees'
              newTab
              className={hstack({ color: 'content.default.level-2', gap: 4 })}
            >
              Trading fee <ExternalLinkIcon />
            </IsomorphicLink>
          </dt>
          <dd>
            <Formatted.Percent value={takerFeeRate} precision={maxDecimalPrecision} css={sectionMainPrice} />
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt>Est. price</dt>
          <dd className={stack({ alignItems: 'flex-end' })}>
            <Formatted.Number
              value={pswapDetail?.bidPrice}
              suffix={quoteCurrency}
              precision={CurrencyPrecisionMapper[quoteCurrency]}
              css={sectionMainPrice}
            />
            {!crossMarginFeatureEnabled && liqShortPrice && (
              <Formatted.Number
                value={liqShortPrice}
                precision={CurrencyPrecisionMapper[quoteCurrency]}
                prefix='Est. liquidation price '
                css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-2' }}
              />
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const sectionDescriptionItem = flex({
  justifyContent: 'space-between',
  '& + &': {
    mt: 12,
  },
});

const sectionMainPrice = {
  textStyle: 'Text/L/Regular',
  color: 'content.default.level-4',
};
